import { useEffect, useState } from "react";

const useInterestRate = (mortgage, term, type) => {
    const [result, setResult] = useState(null);
    const query = new URLSearchParams({
        loanAmount: mortgage,
        months: term * 12,
        monthsFixed: type === "fix" ? 60 : 0,
        matchType: type === "fix" ? "min" : "eq",
    }).toString();

    const baseUrl = process.env.GATSBY_MORTGAGING_API_BASE_URL;
    const enabled = Boolean(baseUrl && mortgage > 0 && term > 0);

    useEffect(() => {
        if (!enabled) return;

        const controller = new AbortController();
        let requestTimeout;
        // Debounce edits and cancel outdated requests so they cannot overwrite the current rate.
        const timeout = setTimeout(async () => {
            requestTimeout = setTimeout(() => {
                setResult({ query, rate: null });
                controller.abort();
            }, 10000);
            try {
                const response = await fetch(`${baseUrl}/engine/web/offer/calculator?${query}`, {
                    signal: controller.signal,
                });
                if (!response.ok) throw new Error("Rate request failed");

                const { interestRate } = await response.json();
                if (!Number.isFinite(interestRate) || interestRate <= 0) throw new Error("Invalid rate");
                if (!controller.signal.aborted) {
                    setResult({ query, rate: String(interestRate).replace(".", ",") });
                }
            } catch {
                if (!controller.signal.aborted) setResult({ query, rate: null });
            } finally {
                clearTimeout(requestTimeout);
            }
        }, 300);

        return () => {
            clearTimeout(timeout);
            clearTimeout(requestTimeout);
            controller.abort();
        };
    }, [baseUrl, enabled, query]);

    const hasResult = result?.query === query;
    return { rate: hasResult ? result.rate : null, isLoading: enabled && !hasResult };
};

export default useInterestRate;
